import { Handshake } from 'lucide-react';
import { ReunionTransaction } from '../../../hooks/useReunionMensuelleData';
import { PeriodBucket, getPeriodKeyForDate, buildPeriodMultiCategoryCounts } from '../../../utils/reunionMensuelle';
import { Scope, getColumnForTransaction } from './ScopeSelector';
import TrendChartCard from './TrendChartCard';

interface RetrocessionTabProps {
  transactions: ReunionTransaction[];
  periods: PeriodBucket[];
  scope: Scope;
}

const CATEGORIES: { key: string; label: string; predicate: (tx: ReunionTransaction) => boolean }[] = [
  { key: 'newco', label: 'Newco', predicate: tx => tx.property_origin === 'newco' },
  { key: 'ks', label: 'KS', predicate: tx => tx.property_origin === 'syndic' },
  { key: 'intercabinet', label: 'Intercabinet', predicate: tx => tx.retrocession_types.includes('intercabinet') },
  { key: 'recommandation', label: 'Recommandation', predicate: tx => tx.retrocession_types.includes('recommandation_interne') },
  { key: 'lefeuvre', label: 'Rétrocession Lefeuvre', predicate: tx => tx.retrocession_types.includes('retrocession_lefeuvre') },
];

export default function RetrocessionTab({ transactions, periods, scope }: RetrocessionTabProps) {
  const inScope = transactions.filter(tx => getColumnForTransaction(tx, scope) !== null);
  const getPeriodKey = (tx: ReunionTransaction) => getPeriodKeyForDate(tx.compromise_date, periods);

  const counts = buildPeriodMultiCategoryCounts(inScope, periods, CATEGORIES, getPeriodKey);
  const columns = CATEGORIES.map(c => ({ key: c.key, label: c.label }));

  const formatCount = (v: number) => v.toString();

  return (
    <TrendChartCard
      icon={<Handshake size={16} className="text-rose-600" />}
      iconBg="bg-rose-50"
      title="Dossiers en rétrocession"
      periods={periods}
      columns={columns}
      data={counts}
      valueFormatter={formatCount}
      getTransactions={(periodKey, columnKey) => {
        const category = CATEGORIES.find(c => c.key === columnKey);
        if (!category) return [];
        return inScope.filter(tx => getPeriodKey(tx) === periodKey && category.predicate(tx));
      }}
      infoText="Chaque vente est rangée dans le mois/trimestre de sa date de compromis. Newco/KS = origine du bien ; Intercabinet/Recommandation/Rétrocession Lefeuvre = type de rétrocession présent sur le dossier. Un même dossier peut compter dans plusieurs catégories à la fois."
    />
  );
}
